import React, {useState} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {Colors} from '../theme/colors';
import {forgotPassword} from '../services/auth';
import {Icon} from '../components/Icon';

type RootStackParamList = {
  Home: undefined;
  Login: undefined;
  ForgotPassword: undefined;
};

type ForgotPasswordScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'ForgotPassword'>;
};

export const ForgotPasswordScreen: React.FC<ForgotPasswordScreenProps> = ({navigation}) => {
  const insets = useSafeAreaInsets();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailFocused, setEmailFocused] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Inserisci la tua email');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    const result = await forgotPassword(email.trim());

    setIsLoading(false);

    if (result.success) {
      setSuccess(result.message || 'Se l\'email è registrata, riceverai un link per reimpostare la password.');
    } else {
      setError(result.message || 'Errore. Riprova.');
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          {/* Purple Header with Logo */}
          <View style={[styles.purpleHeader, {paddingTop: insets.top + 16}]}>
            <TouchableOpacity
              style={[styles.closeButton, {top: insets.top + 8}]}
              onPress={() => navigation.goBack()}
              hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}
            >
              <Icon name="chevron-left" size={20} color="#FFFFFF" />
            </TouchableOpacity>
            <Image
              source={{uri: 'https://cdn.laviola.it/laviola/logo-payoff.png'}}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>

          {/* White Form Area */}
          <View style={styles.formContainer}>
            <View style={styles.form}>
              {/* Title */}
              <View style={styles.titleRow}>
                <Icon name="lock-keyhole" size={22} color={Colors.primary} />
                <Text style={styles.title}>Password dimenticata</Text>
              </View>

              <Text style={styles.subtitle}>
                Inserisci la tua email e ti invieremo un link per reimpostare la password.
              </Text>

              {error ? (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              ) : null}

              {success ? (
                <View style={styles.successContainer}>
                  <Icon name="check" size={20} color="#2E7D32" />
                  <Text style={styles.successText}>{success}</Text>
                </View>
              ) : null}

              {/* Email field */}
              <Text style={styles.inputLabel}>Email</Text>
              <View style={[
                styles.inputContainer,
                emailFocused && styles.inputContainerFocused,
              ]}>
                <Icon
                  name="envelope"
                  size={16}
                  color={emailFocused ? Colors.primary : Colors.textSecondary}
                />
                <TextInput
                  style={styles.input}
                  placeholder=""
                  placeholderTextColor={Colors.textSecondary}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                />
              </View>

              {/* Submit Button */}
              <TouchableOpacity
                style={[styles.submitButton, isLoading && styles.submitButtonDisabled]}
                onPress={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <View style={styles.submitButtonContent}>
                    <Text style={styles.submitButtonText}>Invia link di reset</Text>
                    <Icon name="arrow-right" size={14} color="#FFFFFF" />
                  </View>
                )}
              </TouchableOpacity>
            </View>

            {/* Back to login */}
            <View style={styles.backContainer}>
              <TouchableOpacity onPress={() => navigation.goBack()}>
                <Text style={styles.backLink}>Torna al login</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.primary,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  purpleHeader: {
    backgroundColor: Colors.primary,
    alignItems: 'center',
    paddingBottom: 40,
    paddingHorizontal: 20,
  },
  closeButton: {
    position: 'absolute',
    left: 16,
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.25)',
    borderRadius: 22,
    zIndex: 10,
  },
  logo: {
    width: 200,
    height: 70,
    marginTop: 0,
  },
  formContainer: {
    flex: 1,
    backgroundColor: Colors.background,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    marginTop: -20,
    paddingTop: 28,
    paddingHorizontal: 20,
    paddingBottom: 30,
  },
  form: {
    backgroundColor: Colors.surface,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 6,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.textPrimary,
  },
  subtitle: {
    fontSize: 13,
    color: Colors.textSecondary,
    lineHeight: 18,
    marginBottom: 20,
  },
  errorContainer: {
    backgroundColor: '#FFEBEE',
    padding: 12,
    borderRadius: 8,
    marginBottom: 14,
  },
  errorText: {
    color: Colors.error,
    fontSize: 13,
    textAlign: 'center',
  },
  successContainer: {
    backgroundColor: '#E8F5E9',
    padding: 14,
    borderRadius: 8,
    marginBottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  successText: {
    color: '#2E7D32',
    fontSize: 13,
    flex: 1,
    lineHeight: 18,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.textPrimary,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.surfaceVariant,
    borderRadius: 10,
    paddingHorizontal: 14,
    marginBottom: 20,
    gap: 10,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  inputContainerFocused: {
    borderColor: Colors.primary,
    backgroundColor: Colors.surface,
  },
  input: {
    flex: 1,
    paddingVertical: 14,
    fontSize: 16,
    color: Colors.textPrimary,
  },
  submitButton: {
    backgroundColor: Colors.primary,
    paddingVertical: 16,
    borderRadius: 28,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  backContainer: {
    alignItems: 'center',
    marginTop: 20,
  },
  backLink: {
    fontSize: 14,
    color: Colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
